import { useEffect, useState } from "react";
import { http } from "@/api/client";
import { toast } from "sonner";
import { Key, Save, Trash2, Sparkles, ShieldCheck, Info, Download, Upload } from "lucide-react";

const MODELS = [
  { id: "gpt-4o-mini", label: "GPT-4o mini (rapide, économique)" },
  { id: "gpt-4o", label: "GPT-4o (qualité supérieure)" },
  { id: "gpt-4.1-mini", label: "GPT-4.1 mini" },
  { id: "gpt-4.1", label: "GPT-4.1" },
  { id: "gpt-5.2", label: "GPT-5.2 (dernier)" },
];

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [s, setS] = useState({ openai_api_key_masked: "", openai_api_key_set: false, openai_model: "gpt-4o-mini" });
  const [newKey, setNewKey] = useState("");
  const [model, setModel] = useState("gpt-4o-mini");
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await http.get("/admin/settings");
      setS(r.data);
      setModel(r.data.openai_model || "gpt-4o-mini");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = { openai_model: model };
      if (newKey.trim()) payload.openai_api_key = newKey.trim();
      const r = await http.put("/admin/settings", payload);
      setS(r.data);
      setNewKey("");
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  }

  async function removeKey() {
    if (!window.confirm("Supprimer la clé OpenAI enregistrée ? La génération IA retombera sur la clé Emergent par défaut.")) return;
    try {
      const r = await http.put("/admin/settings", { openai_api_key: "" });
      setS(r.data);
      toast.success("Clé supprimée");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  }

  async function exportAll() {
    try {
      const r = await http.get("/admin/export");
      const json = JSON.stringify(r.data, null, 2);
      const d = new Date().toISOString().slice(0, 10);
      const filename = `ddsbillancourt-export-${d}.json`;
      const { courses, questions, intros } = r.data.counts;

      // Try native download via Blob + anchor
      let ok = false;
      try {
        const blob = new Blob([json], { type: "application/json;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.rel = "noopener";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1500);
        ok = true;
      } catch (err) {
        console.error("Blob download failed, falling back to data URL", err);
      }

      // Fallback: open as data URL in a new tab if the browser blocked the download
      if (!ok) {
        const dataUrl = "data:application/json;charset=utf-8," + encodeURIComponent(json);
        const w = window.open(dataUrl, "_blank");
        if (!w) {
          toast.error("Téléchargement bloqué par le navigateur — autorisez les pop-ups ou désactivez l'anti-pub.");
          return;
        }
      }

      toast.success(`Export prêt · ${courses} cours · ${questions} questions · ${intros} intros`);
    } catch (e) {
      console.error("Export error", e);
      toast.error(e?.response?.data?.detail || "Erreur d'export");
    }
  }

  async function importFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    let data;
    try {
      const text = await file.text();
      data = JSON.parse(text);
    } catch {
      toast.error("Fichier JSON invalide"); return;
    }
    const mode = window.confirm(
      `Importer ${data.counts?.courses || 0} cours, ${data.counts?.questions || 0} questions, ${data.counts?.intros || 0} intros ?\n\nOK = Fusion (upsert, conserve l'existant non touché)\nAnnuler = choisir "Remplacer"`
    ) ? "merge" : null;
    let finalMode = mode;
    if (!finalMode) {
      if (!window.confirm("REMPLACER toutes les questions des cours présents dans le fichier ? (les cours absents du fichier ne sont pas touchés)")) return;
      finalMode = "replace_questions";
    }
    try {
      const r = await http.post("/admin/import", { ...data, mode: finalMode });
      const st = r.data.stats;
      toast.success(`Import OK · ${st.courses_upserted} cours · ${st.questions_inserted} questions · ${st.intros_upserted} intros`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Erreur d'import"); }
  }

  if (loading) return <div className="text-[#5b6577]" data-testid="settings-loading">Chargement…</div>;

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <div>
        <h1 className="font-headings font-black text-3xl md:text-4xl text-[#1a2332]">Paramètres</h1>
        <p className="mt-1 text-[#5b6577]">Configuration de l'IA utilisée par le générateur de quiz.</p>
      </div>

      <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 bg-gradient-to-br from-[#fff7f0] to-white border-b border-black/5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#FF8C42]/10 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-[#FF8C42]" />
          </div>
          <div>
            <div className="font-headings font-bold text-[#1a2332]">Clé API OpenAI (ChatGPT)</div>
            <div className="text-sm text-[#5b6577] mt-0.5">
              Si renseignée, elle sera utilisée pour la génération des quiz. Sinon, la clé Emergent par défaut est utilisée.
            </div>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {s.openai_api_key_set ? (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#00a86b]/5 border border-[#00a86b]/20" data-testid="key-status-set">
              <ShieldCheck className="w-5 h-5 text-[#00a86b] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-[#007a4e]">Clé enregistrée</div>
                <div className="text-xs font-mono text-[#5b6577] truncate">{s.openai_api_key_masked}</div>
              </div>
              <button onClick={removeKey} data-testid="remove-key-btn"
                className="inline-flex items-center gap-1 text-xs bg-white border border-[#e21b3c]/30 text-[#e21b3c] rounded-lg px-3 py-1.5 hover:bg-[#e21b3c]/5">
                <Trash2 className="w-3.5 h-3.5" /> Supprimer
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 p-3 rounded-xl bg-[#f8faff] border border-black/5" data-testid="key-status-unset">
              <Info className="w-5 h-5 text-[#5b6577] shrink-0" />
              <div className="text-sm text-[#5b6577]">Aucune clé personnalisée — la clé Emergent par défaut sera utilisée.</div>
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-[#1a2332] mb-1.5">
              {s.openai_api_key_set ? "Remplacer la clé" : "Nouvelle clé OpenAI"}
            </label>
            <div className="flex items-center gap-2">
              <Key className="w-4 h-4 text-[#5b6577]" />
              <input type="password" placeholder="sk-..." value={newKey} onChange={e=>setNewKey(e.target.value)}
                className="flex-1 bg-[#f8faff] border border-[#e5eaf3] rounded-lg px-3 py-2 text-sm text-[#1a2332] font-mono"
                data-testid="openai-key-input" autoComplete="off" />
            </div>
            <p className="text-xs text-[#9aa5bc] mt-1.5">
              Créez votre clé sur <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="text-[#FF8C42] hover:underline">platform.openai.com/api-keys</a>. La clé est stockée uniquement sur votre serveur et n'est jamais renvoyée en clair.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1a2332] mb-1.5">Modèle à utiliser</label>
            <select value={model} onChange={e=>setModel(e.target.value)} data-testid="openai-model-select"
              className="w-full bg-[#f8faff] border border-[#e5eaf3] rounded-lg px-3 py-2 text-sm text-[#1a2332]">
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <p className="text-xs text-[#9aa5bc] mt-1.5">Valide uniquement avec votre clé OpenAI personnalisée. Sans clé, le modèle par défaut Emergent est utilisé.</p>
          </div>

          <div className="flex justify-end pt-2">
            <button onClick={save} disabled={saving} data-testid="save-settings-btn"
              className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm disabled:opacity-60">
              <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </div>
      </div>

      {/* Export / Import */}
      <div className="bg-white border border-black/5 rounded-2xl shadow-sm overflow-hidden" data-testid="export-card">
        <div className="p-5 bg-gradient-to-br from-[#f0fff7] to-white border-b border-black/5 flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#00a86b]/10 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-[#00a86b]" />
          </div>
          <div>
            <div className="font-headings font-bold text-[#1a2332]">Export / Import du contenu</div>
            <div className="text-sm text-[#5b6577] mt-0.5">
              Téléchargez l'intégralité des cours, quiz et introductions dans un fichier JSON. Idéal pour sauvegarde, migration entre environnements (preview ↔ prod) ou réinjection.
            </div>
          </div>
        </div>
        <div className="p-5 flex items-center gap-3 flex-wrap">
          <button onClick={exportAll} data-testid="export-all-btn"
            className="inline-flex items-center gap-2 bg-[#00a86b] hover:bg-[#008855] text-white font-headings font-bold rounded-xl px-5 py-2.5 text-sm">
            <Download className="w-4 h-4" /> Exporter tout (.json)
          </button>
          <label className="inline-flex items-center gap-2 bg-white border border-[#e5eaf3] hover:bg-[#f8faff] text-[#1a2332] font-headings font-bold rounded-xl px-5 py-2.5 text-sm cursor-pointer" data-testid="import-label">
            <Upload className="w-4 h-4" /> Importer un fichier…
            <input type="file" accept="application/json,.json" onChange={importFile} className="hidden" data-testid="import-file-input" />
          </label>
          <p className="text-xs text-[#9aa5bc] flex-1 min-w-[200px]">
            L'export inclut : cours (36 max), questions + explications + niveaux, intros compétences.
          </p>
        </div>
      </div>
    </div>
  );
}
